import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

//styles
import { DeliveryAddress, Container } from "./styles";

// constants
import { DEFAULT_ACCESSIBILITY_TEXTS, SUB_TEXT_TIMEOUT, TICK_STATES } from "../constants";
import { ACCESSIBILITY_ROLES } from "@swiggy-private/checkout-core";
import { TICK_SCREEN_TEST_IDS } from "@swiggy-private/payment-test-ids";

// interfaces
import { ITransitionScreen } from "../interfaces";

// components
import TransitionDeliveryAddress from "@payment-ui/widgets/transition-delivery-address";
import TransitionPlayer from "./TransitionPlayer";
import SavingText from "./components/SavingText";
import { BlackMembershipBenefits } from "./components/BlackMembershipBenefits";
import Footer from "./Footer";

// helpers
import { getAddressAnotationAsset, getPostSuccessDelay, hasInvalidAddressCategory } from "../helpers";
import { useEffectOnce } from "@swiggy-private/core-ui";

//functions
function TransitionScreen({
    tickState,
    postSuccessCb,
    successTimeout = SUB_TEXT_TIMEOUT,
    customSuccessScreen = false,
    deliveryAddress,
    a11yTexts,
    onError,
    getSuperMessage,
    isOfferBundleApplied,
    isMilestoneUnlockEligible,
    onTransitionScreenView,
    recordTransitionScreenLoadTime,
    isSwiggyBlackApplicable,
    isSwiggyBlackOtpAnimationApplicable,
    isPopCart,
    successFooterMessage,
    isBoltCartM3,
    isAffordableCartM2,
    isToingFlow,
    isGiftingCart,
}: ITransitionScreen) {
    const [isPostEventFired, setPostEventFired] = useState(false);
    const [showContent, setShowContent] = useState(false);
    const [state, setTickState] = useState<TICK_STATES>(tickState);
    const [blackBenefitsTimeout, setBlackBenefitsTimeout] = useState<number>(0);

    const shouldShowBlackBenefits = useMemo(
        () => isSwiggyBlackApplicable && state === TICK_STATES.SUCCESS,
        [isSwiggyBlackApplicable, state],
    );

    const shouldShowBoltBenefits = useMemo(() => isBoltCartM3 && state === TICK_STATES.SUCCESS, [isBoltCartM3, state]);

    const shouldShowAffordableBenefits = useMemo(
        () => isAffordableCartM2 && state === TICK_STATES.SUCCESS,
        [isAffordableCartM2, state],
    );

    const showGiftingPostSuccessCallout = useMemo(
        () => isGiftingCart && state === TICK_STATES.SUCCESS,
        [isGiftingCart, state],
    );

    const showDefaultTickScreen = useMemo(
        () =>
            !shouldShowBlackBenefits &&
            !shouldShowBoltBenefits &&
            !shouldShowAffordableBenefits &&
            !showGiftingPostSuccessCallout,
        [shouldShowBlackBenefits, shouldShowBoltBenefits, shouldShowAffordableBenefits, showGiftingPostSuccessCallout],
    );

    useEffect(() => {
        if (tickState !== TICK_STATES.SUCCESS) {
            return;
        }
        // delay confirm tick
        const timeOut = window.setTimeout(() => {
            setTickState(tickState);
        }, 1000);

        return (): void => clearTimeout(timeOut);
    }, [tickState]);

    const onPostSuccess = useCallback((): void => {
        setPostEventFired(true);
        postSuccessCb && postSuccessCb();
    }, [postSuccessCb]);

    const callbackfn = useCallback(() => {
        if (isPostEventFired) {
            return;
        }
        onPostSuccess();
    }, [isPostEventFired, onPostSuccess]);

    // post success
    useEffect(() => {
        if (state !== TICK_STATES.SUCCESS || typeof postSuccessCb !== "function") {
            return;
        }

        if (customSuccessScreen) {
            onPostSuccess();
        }

        // manually firing post success
        const postSuccessDelay = getPostSuccessDelay({
            isSwiggyBlackApplicable,
            blackBenefitsTimeout,
            successTimeout,
            isBoltCartM3,
            isAffordableCartM2,
            isGiftingCart,
        });
        const timeout = setTimeout(callbackfn, postSuccessDelay);

        return (): void => {
            clearTimeout(timeout);
        };
    }, [
        state,
        postSuccessCb,
        onPostSuccess,
        callbackfn,
        customSuccessScreen,
        successTimeout,
        isSwiggyBlackApplicable,
        blackBenefitsTimeout,
        isBoltCartM3,
        isAffordableCartM2,
        isGiftingCart,
    ]);

    const onDOMLoaded = useCallback(() => {
        setShowContent(true);
    }, []);

    const a11yStatus = useMemo(() => {
        const a11Texts = a11yTexts != null ? a11yTexts : DEFAULT_ACCESSIBILITY_TEXTS;
        return state == TICK_STATES.SUCCESS ? a11Texts[TICK_STATES.SUCCESS] : a11Texts[TICK_STATES.LOADING];
    }, [a11yTexts, state]);

    const savings = useMemo(() => getSuperMessage?.(), [getSuperMessage]);

    const category = useMemo(() => {
        return hasInvalidAddressCategory(deliveryAddress) ? deliveryAddress?.addressCategory : deliveryAddress?.userIntentAddressCategory;
    }, [deliveryAddress]);

    const deliveryAddressAnotation = useMemo(() => getAddressAnotationAsset(deliveryAddress, category), [
        deliveryAddress,
        category,
    ]);

    useEffectOnce(() => {
        onTransitionScreenView?.();
    });

    return (
        <Container
            data-testid={TICK_SCREEN_TEST_IDS.CONTAINER}
            tabIndex={0}
            role={ACCESSIBILITY_ROLES.ALERT}
            aria-live="assertive"
            aria-label={a11yStatus}
        >
            {showDefaultTickScreen && (
                <SavingText
                    savings={savings}
                    tickState={state}
                    isFreeDel={isOfferBundleApplied}
                    isMilestoneUnlockEligible={isMilestoneUnlockEligible}
                    isToingFlow={isToingFlow}
                />
            )}
            <TransitionPlayer
                onDOMLoaded={onDOMLoaded}
                onError={onError}
                deliveryAddressAnotation={deliveryAddressAnotation}
                tickState={state}
                recordTransitionScreenLoadTime={recordTransitionScreenLoadTime}
                isSwiggyBlackApplicable={isSwiggyBlackApplicable}
                isSwiggyBlackOtpAnimationApplicable={isSwiggyBlackOtpAnimationApplicable}
                isPopCart={isPopCart}
                isBoltCartM3={isBoltCartM3}
                isAffordableCartM2={isAffordableCartM2}
                isToingFlow={isToingFlow}
                isGiftingCart={isGiftingCart}
            />
            <DeliveryAddress show={showContent} data-testid={TICK_SCREEN_TEST_IDS.ADDRESS_TEXT}>
                {!shouldShowAffordableBenefits && (shouldShowBlackBenefits || shouldShowBoltBenefits) && (
                    <BlackMembershipBenefits
                        successMessageInfoV2={savings?.successMessageInfo}
                        setBlackBenefitsTimeout={setBlackBenefitsTimeout}
                        isBolt={shouldShowBoltBenefits}
                    />
                )}

                {showDefaultTickScreen && <TransitionDeliveryAddress deliveryAddress={deliveryAddress} />}
            </DeliveryAddress>

            <Footer successFooterMessage={successFooterMessage} showContent={showContent} tickState={state} />
        </Container>
    );
}

export default memo(TransitionScreen);
